/**
 * Замечания контроля качества по клиенту (этот чат и связанные), новые сверху, с путём до базы
 * знаний. В чате клиента — последняя вкладка панели «Сделка · Договор · … · Замечания»
 * (`?panel=qc`, `QcList`); в группе панели сделки нет — своя панель по значку в шапке (`QcPanel`).
 * Бейдж у сообщения открывает её с замечанием в фокусе.
 */
import { useGetIdentity } from "@refinedev/core";
import { ClipboardCheck, X } from "lucide-react";
import { useEffect, useState } from "react";

import { useQc } from "@/components/qc/QcSelection";
import { RemarkCard } from "@/components/qc/RemarkCard";
import { KNOWLEDGE_EDIT, useCan } from "@/lib/access";
import type { CurrentUser } from "@/lib/providers";
import { chatRemarksKey, needsAttention } from "@/lib/qc";

/** Панель поверх ленты (уже 1280 px) — перед переходом к сообщению её закрываем. */
const narrow = () => window.innerWidth < 1280;

/** Список замечаний с «Все / Мои» и «Новое замечание»; `onJump` — перед переходом к сообщению. */
export function QcList({ focus, onJump }: { focus: number | null; onJump: () => void }) {
  const qc = useQc();
  const editor = useCan(KNOWLEDGE_EDIT);
  const { data: me } = useGetIdentity<CurrentUser>();
  const [mine, setMine] = useState(false);
  useEffect(() => {
    if (focus) document.getElementById(`qc-remark-${focus}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focus, qc?.remarks.length]);
  if (!qc) return null;
  const list = qc.remarks.filter((r) => !mine || r.user_id === me?.id);
  return (
    <div className="flex flex-col gap-2 p-3" data-annot="список замечаний">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-full bg-bg-subtle p-0.5 text-[12px]">
          {[
            [false, "Все"],
            [true, "Мои"],
          ].map(([v, label]) => (
            <button
              key={String(label)}
              type="button"
              onClick={() => setMine(v as boolean)}
              className={`rounded-full px-2.5 py-0.5 ${mine === v ? "bg-bg font-medium shadow-sm" : "text-fg-muted"}`}
            >
              {label}
            </button>
          ))}
        </div>
        {qc.add && !(qc.active && qc.purpose === "remark") && (
          <button
            type="button"
            onClick={() => {
              qc.start();
              if (narrow()) onJump(); // на телефоне панель закрывает ленту — отмечать там
            }}
            className="ml-auto rounded-full bg-accent px-3 py-1 text-[13px] font-medium text-white hover:opacity-90"
            title="Включить отметки у сообщений и звонков ленты"
          >
            Новое замечание
          </button>
        )}
      </div>
      {qc.active && qc.purpose === "remark" && <p className="text-[13px] text-fg-muted">Отметьте сообщения и звонки в ленте кружками слева и опишите замечание внизу.</p>}
      {list.length === 0 && <p className="text-[13px] text-fg-muted">Замечаний по этому клиенту пока нет.</p>}
      {list.map((r) => (
        <div key={r.id} id={`qc-remark-${r.id}`}>
          <RemarkCard
            r={r}
            focused={focus === r.id}
            inChat
            editor={editor}
            invalidate={[...chatRemarksKey(qc.chatId)]}
            onJump={() => narrow() && onJump()}
          />
        </div>
      ))}
    </div>
  );
}

/** Число на вкладке «Замечания»: всего и «!» — требуют внимания. */
export function QcTabCount() {
  const qc = useQc();
  if (!qc?.remarks.length) return null;
  const attention = qc.remarks.filter(needsAttention).length;
  return (
    <span
      className={`rounded-full px-1.5 text-[12px] font-medium leading-[18px] text-white ${attention ? "bg-[#e53935]" : "bg-[var(--tg-primary)]"}`}
      title={`Замечаний ${qc.remarks.length}${attention ? `, требуют внимания ${attention}` : ""}`}
    >
      {qc.remarks.length}
      {attention > 0 && ` · !${attention}`}
    </span>
  );
}

/** Своя панель — только в группах сотрудников: там нет панели сделки с вкладками. */
export function QcPanel({ focus, onClose }: { focus: number | null; onClose: () => void }) {
  const qc = useQc();
  if (!qc) return null;
  return (
    <aside
      className="fixed inset-0 z-40 flex flex-col overflow-hidden bg-bg text-fg max-xl:shadow-2xl sm:left-auto sm:w-[440px] xl:static xl:z-auto xl:w-[440px] xl:flex-none xl:rounded-[24px] xl:shadow-[0_0_4px_rgba(0,0,0,.24)]"
      data-annot="панель замечаний"
    >
      <div className="flex h-11 flex-none items-center gap-2 border-b border-border px-3 text-[13.5px]">
        <ClipboardCheck size={16} className="text-fg-muted" />
        <span className="font-medium">Замечания {qc.remarks.length || ""}</span>
        <button type="button" onClick={onClose} title="Скрыть" className="ml-auto rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-fg">
          <X size={16} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <QcList focus={focus} onJump={onClose} />
      </div>
    </aside>
  );
}
